import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Building2, Lock, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AltchaWidget from "../components/AltchaWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASON_MESSAGES = {
  invalid_format: "El enlace recibido no es válido.",
  not_found: "Este enlace no existe o ya fue invalidado.",
  already_used: "Este enlace ya fue utilizado. Por seguridad solicite uno nuevo.",
  expired: "El enlace expiró (vigencia 15 minutos). Solicite uno nuevo.",
  user_invalid: "La cuenta asociada no es válida o no es de tipo contribuyente.",
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [altchaPayload, setAltchaPayload] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Validar el token al cargar
  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      if (!token) {
        setTokenError("invalid_format");
        setValidating(false);
        return;
      }
      try {
        const resp = await axios.post(`${API}/auth/reset-password/validate`, { token });
        if (cancelled) return;
        if (resp.data?.valid) {
          setTokenInfo(resp.data);
        } else {
          setTokenError(resp.data?.reason || "not_found");
        }
      } catch {
        if (cancelled) return;
        setTokenError("not_found");
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    validate();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!altchaPayload) {
      toast.error("Por favor complete la verificación anti-bot ALTCHA");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
        altcha_payload: altchaPayload,
      });
      setDone(true);
      toast.success(resp.data?.message || "Contraseña restablecida correctamente");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "No se pudo restablecer la contraseña.";
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof detail === "object") {
        msg = detail.message || msg;
        if (Array.isArray(detail.errors) && detail.errors.length) {
          msg += ": " + detail.errors.join(" · ");
        }
      }
      toast.error(msg);
      setAltchaPayload("");
    } finally {
      setLoading(false);
    }
  };

  // Estado: validando token
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Validando enlace seguro...</p>
        </div>
      </div>
    );
  }

  // Estado: token inválido / expirado / usado
  if (tokenError) {
    return (
      <div className="min-h-screen flex login-pattern">
        <div className="w-full flex items-center justify-center p-6 bg-slate-50">
          <Card className="w-full max-w-md shadow-xl border-0" data-testid="reset-error-card">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Enlace no válido
              </CardTitle>
              <CardDescription className="text-slate-500">
                {REASON_MESSAGES[tokenError] || "No se pudo validar el enlace."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full bg-blue-800 hover:bg-blue-900 text-white"
                data-testid="reset-request-new"
              >
                Solicitar un nuevo enlace
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="w-full"
                data-testid="reset-back-login"
              >
                Volver al inicio de sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Estado: éxito
  if (done) {
    return (
      <div className="min-h-screen flex login-pattern">
        <div className="w-full flex items-center justify-center p-6 bg-slate-50">
          <Card className="w-full max-w-md shadow-xl border-0" data-testid="reset-success-card">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ¡Contraseña actualizada!
              </CardTitle>
              <CardDescription className="text-slate-500">
                Su nueva contraseña fue registrada con éxito. Por seguridad cerramos todas sus sesiones anteriores.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="reset-go-login"
              >
                Iniciar sesión con la nueva clave
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Estado: formulario
  return (
    <div className="min-h-screen flex login-pattern">
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Building2 className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Crear nueva contraseña
          </h1>
          <p className="text-lg text-slate-300">
            Defina una contraseña fuerte. Recomendamos al menos 12 caracteres combinando mayúsculas, números y símbolos.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md shadow-xl border-0 animate-fade-in" data-testid="reset-password-card">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Restablecer contraseña
            </CardTitle>
            <CardDescription className="text-slate-500">
              {tokenInfo?.email_masked ? (
                <>Cuenta: <strong>{tokenInfo.email_masked}</strong></>
              ) : (
                "Ingrese y confirme su nueva contraseña"
              )}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="new-pwd" className="text-slate-700">Nueva contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new-pwd"
                    data-testid="reset-new-password"
                    type={showPwd ? "text" : "password"}
                    placeholder="Mínimo 8 caracteres"
                    className="pl-10 pr-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPwd(!showPwd)}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-pwd" className="text-slate-700">Confirmar contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm-pwd"
                    data-testid="reset-confirm-password"
                    type={showPwd ? "text" : "password"}
                    placeholder="Repita la contraseña"
                    className="pl-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-600">Las contraseñas no coinciden</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-slate-700 space-y-1">
                <p><strong>Recomendaciones de seguridad:</strong></p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Use mayúsculas, minúsculas, números y símbolos.</li>
                  <li>Evite información personal (nombre, cédula, fechas).</li>
                  <li>No reutilice contraseñas anteriores.</li>
                </ul>
              </div>

              <AltchaWidget onVerified={setAltchaPayload} testid="altcha-reset-password" />

              <Button
                type="submit"
                className="w-full text-white bg-green-600 hover:bg-green-700"
                data-testid="reset-submit"
                disabled={loading || !altchaPayload || !newPassword || newPassword !== confirmPassword}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Actualizando...
                  </span>
                ) : "Actualizar contraseña"}
              </Button>

              <p className="text-center text-xs text-slate-500 pt-3 border-t border-slate-100">
                <Link to="/login" className="text-blue-700 font-semibold underline" data-testid="reset-cancel">
                  Cancelar y volver al inicio
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
